
import React from 'react';
import { Brain } from 'lucide-react';
import { Language } from '../types';
import { TRANSLATIONS } from '../constants';

interface SessionTimerProps {
  timeLeft: number;
  isActive: boolean;
  totalDuration: number;
  onToggle: () => void;
  onSetDuration: (minutes: number) => void;
  isAutoPilot: boolean;
  onAutoPilotToggle: () => void;
  language: Language;
}

const SessionTimer: React.FC<SessionTimerProps> = ({ 
  timeLeft, 
  isActive, 
  onToggle,
  isAutoPilot,
  onAutoPilotToggle,
  language
}) => {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const t = TRANSLATIONS[language];

  return (
    <div className="glass p-2 md:p-4 rounded-[1.5rem] md:rounded-2xl flex flex-col items-center justify-center relative overflow-hidden h-full min-h-[120px] md:min-h-[220px]">
      <div className="flex items-center gap-2 text-[8px] md:text-[11px] font-mono-cia text-slate-500 uppercase tracking-widest mb-1 md:mb-2 font-black">
        <Brain size={12} className="text-blue-400 animate-pulse" />
        {t.timer.sync}
      </div>
      
      <div className="text-4xl md:text-7xl font-mono-cia font-black text-white tracking-tighter mb-2 md:mb-4 tabular-nums drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">
        {formatTime(timeLeft)}
      </div>

      <div className="flex items-center gap-2 opacity-40 mb-2 md:mb-4">
        <span className="text-blue-400 animate-pulse">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
        </span>
        <span className="text-[7px] md:text-[10px] font-black text-blue-400 uppercase tracking-widest">{t.timer.headphones}</span>
      </div>

      <div className="w-full flex flex-col gap-1.5 md:gap-2 max-w-xl">
         {isActive && !isAutoPilot && (
           <button
            onClick={onToggle}
            className="w-full py-2 md:py-4 rounded-xl font-black transition-all shadow-2xl active:scale-95 flex items-center justify-center gap-2 text-[8px] md:text-[12px] uppercase tracking-[0.1em] bg-red-900/40 border border-red-500/50 text-red-200"
          >
            {t.timer.terminate}
          </button>
        )}

        <button 
          onClick={onAutoPilotToggle} 
          className={`w-full py-2 md:py-4 rounded-xl font-black transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 text-[7px] md:text-[10px] uppercase border border-white/10 ${
            isAutoPilot 
              ? 'bg-emerald-600 text-white shadow-emerald-500/20' 
              : 'bg-slate-800 text-slate-400 shadow-black/40'
          }`}
        >
          {isAutoPilot ? t.timer.autopilot_active : t.timer.autopilot_activate}
        </button>
      </div>
      
      {isAutoPilot && (
        <div className="mt-2 text-[8px] md:text-[10px] font-black text-emerald-400 uppercase tracking-widest animate-pulse">
          {t.timer.auto_sequence}
        </div>
      )}
    </div>
  );
};

export default SessionTimer;
